import {
  faAlignLeft,
  faCircleChevronDown,
  faCog,
  faInfoCircle,
  faListUl,
  faPlus,
  faTrashCan,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  FieldPath,
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import slugify from "slugify";

import { Input } from "src/base-components/Input";
import { CustomPopover } from "src/base-components/Popover";
import {
  getSchemaTypeIcon,
  isBaseSchemaType,
  TypeOption,
} from "src/base-components/SchemaTypeSelector";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import {
  SortableField,
  SortableFieldsContext,
} from "src/base-components/schemaKit/SortableFields";
import { TemplateDescriptionEditor } from "src/cases/templates/common/TemplateDescriptionEditor";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import {
  CaseTemplateFormFieldPath,
  useCheckFieldPermission,
} from "src/cases/templates/useCheckFieldPermission";
import {
  getFieldError,
  useIsTemplateVersionPublished,
} from "src/cases/templates/utils";
import { CaseTemplateFormFieldsInner } from "src/clients/case-management";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

type TemplateFormField = CaseTemplateFormFieldsInner;
type FieldType = TemplateFormField["type"] | "multi_line_text";
type ItemType = "action" | "task";
type BasePath = "case_actions" | "case_tasks";

const FIELD_TYPES = [
  "text",
  "multi_line_text",
  "multi_select",
  "select",
  "number",
  "integer",
  "date",
] as const satisfies readonly FieldType[];

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Text",
  multi_line_text: "Multi line text",
  multi_select: "Multi select",
  select: "Select",
  number: "Number",
  integer: "Integer",
  date: "Date",
};

const EXTENDED_FIELD_TYPE_ICON: Partial<Record<FieldType, IconDefinition>> = {
  text: faAlignLeft,
  multi_line_text: faAlignLeft,
  select: faCircleChevronDown,
  multi_select: faListUl,
};

const getFieldTypeOption = () =>
  FIELD_TYPES.map((type) => {
    const typeOption = isBaseSchemaType(type) ? (
      <div className="flex items-center gap-x-1.5">
        <Icon icon={getSchemaTypeIcon(type)} size="xs" />
        <div className="truncate">{FIELD_TYPE_LABELS[type]}</div>
      </div>
    ) : (
      <TypeOption
        icon={EXTENDED_FIELD_TYPE_ICON[type]}
        label={FIELD_TYPE_LABELS[type]}
      />
    );

    return {
      key: type,
      value:
        type === "multi_line_text" ? (
          <Tooltip
            placement="right"
            title="Ideal for longer inputs like descriptions, summaries, or comments."
            asChild
          >
            <div>{typeOption}</div>
          </Tooltip>
        ) : (
          typeOption
        ),
    };
  });

const FIELD_TYPE_OPTIONS = getFieldTypeOption();

type TemplateFormEditorProps = {
  itemIndex: number;
  itemType: ItemType;
};

export const TemplateFormEditor: React.FC<TemplateFormEditorProps> = ({
  itemIndex,
  itemType,
}) => {
  const form = useFormContext<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();
  const isPublished = useIsTemplateVersionPublished();
  const basePath: BasePath =
    itemType === "action" ? "case_actions" : "case_tasks";
  const { errors } = useFormState<CaseTemplateFormValues>();
  const fieldValues = useWatch({
    name: `attributes.${basePath}.${itemIndex}.form.fields` as FieldPath<CaseTemplateFormValues>,
  }) as { display_as_textarea?: boolean }[] | undefined;

  const getFieldPermission = (fieldIndex: number, field: string) => {
    return hasPermission(
      "edit",
      `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.${field}` as CaseTemplateFormFieldPath,
    );
  };

  const canAddField = hasPermission(
    "add",
    `attributes.${basePath}.${itemIndex}.form.fields` as CaseTemplateFormFieldPath,
  );
  const canDeleteField = hasPermission(
    "delete",
    `attributes.${basePath}.${itemIndex}.form.fields` as CaseTemplateFormFieldPath,
  );

  const { fields, append, move, remove } = useFieldArray<
    CaseTemplateFormValues,
    `attributes.${typeof basePath}.${typeof itemIndex}.form.fields`,
    "_id"
  >({
    name: `attributes.${basePath}.${itemIndex}.form.fields`,
    keyName: "_id",
  });

  const onAddField = () =>
    append(
      {
        type: "text" as const,
        key: "",
        display_name: "",
        required: false,
        description: "",
      },
      {
        focusName: `attributes.${basePath}.${itemIndex}.form.fields.${fields.length}.display_name`,
      },
    );

  return (
    <div className="flex flex-col gap-y-4" data-loc="template-form-editor">
      <SortableFieldsContext
        items={fields.map((field) => field._id)}
        onChangeOrder={({ oldIndex, newIndex }) => {
          move(oldIndex, newIndex);
        }}
      >
        {fields.map((field, fieldIndex) => (
          <SortableField key={field._id} id={field._id}>
            <EditorFieldGroupFrame
              dataLoc={`editor-form-field-${fieldIndex}`}
              deleteButton={
                canDeleteField && (
                  <Tooltip
                    disabled={fields.length > 1}
                    title={
                      fields.length <= 1
                        ? "Forms must have at least one field."
                        : undefined
                    }
                    asChild
                  >
                    <IconButton
                      dataLoc={`form-field-${fieldIndex}-delete-button`}
                      disabled={!canDeleteField || fields.length <= 1}
                      icon={faTrashCan}
                      size="sm"
                      onClick={() => {
                        if (canDeleteField) return remove(fieldIndex);
                      }}
                    />
                  </Tooltip>
                )
              }
              description={
                <div className="flex flex-1 flex-col">
                  <Controller
                    control={form.control}
                    name={`attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.description`}
                    render={({ field, fieldState }) => (
                      <>
                        <TemplateDescriptionEditor
                          data-loc={`form-field-${fieldIndex}-description`}
                          disabled={
                            !getFieldPermission(fieldIndex, "description")
                          }
                          placeholder="Add a description"
                          value={field.value ?? ""}
                          onChange={(value) => {
                            field.onChange(value);
                            form.trigger(
                              `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.description`,
                            );
                          }}
                        />
                        {fieldState.error && (
                          <ErrorHint>{fieldState.error.message}</ErrorHint>
                        )}
                      </>
                    )}
                  />
                </div>
              }
              displayName={{
                input: (
                  <div className="flex flex-1 flex-col">
                    <Input
                      data-loc={`form-field-${fieldIndex}-display-name`}
                      disabled={!getFieldPermission(fieldIndex, "display_name")}
                      errored={
                        !!getFieldError(
                          errors,
                          `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.display_name` as FieldPath<CaseTemplateFormValues>,
                        )
                      }
                      {...form.register(
                        `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.display_name`,
                        {
                          onChange: (event) => {
                            const { isTouched } = form.getFieldState(
                              `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.key`,
                            );

                            if (!isTouched && !isPublished) {
                              form.setValue(
                                `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.key`,
                                slugify(event.target.value, {
                                  lower: true,
                                  strict: true,
                                  replacement: "_",
                                }),
                                { shouldValidate: true },
                              );
                            }
                          },
                          deps: `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.key`,
                        },
                      )}
                      placeholder="Display Name"
                      fullWidth
                    />
                  </div>
                ),
                error: (
                  <EditorFieldErrorHint
                    keyPath={`attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.display_name`}
                  />
                ),
              }}
              fieldKey={
                <div className="flex flex-1 flex-col">
                  <Input
                    data-loc={`form-field-${fieldIndex}-key`}
                    disabled={!getFieldPermission(fieldIndex, "key")}
                    errored={
                      !!getFieldError(
                        errors,
                        `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.key` as FieldPath<CaseTemplateFormValues>,
                      )
                    }
                    {...form.register(
                      `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.key`,
                      {
                        deps: `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.display_name`,
                      },
                    )}
                    placeholder="Key"
                    suffixIcon={{
                      icon: faInfoCircle,
                      helpText:
                        "A unique identifier for this field, to be referenced in the Decision Flow.",
                    }}
                    fullWidth
                    monospaced
                  />
                  <EditorFieldErrorHint
                    keyPath={`attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.key`}
                  />
                </div>
              }
              optionsEditor={
                <FieldOptionsEditorWrapper
                  basePath={basePath}
                  canAddOption={hasPermission(
                    "add",
                    `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options` as CaseTemplateFormFieldPath,
                  )}
                  canDeleteOption={hasPermission(
                    "delete",
                    `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options` as CaseTemplateFormFieldPath,
                  )}
                  canEditOption={getFieldPermission(fieldIndex, "options")}
                  fieldIndex={fieldIndex}
                  itemIndex={itemIndex}
                />
              }
              settings={
                <EditorFieldSettings
                  basePath={basePath}
                  disabled={!getFieldPermission(fieldIndex, "required")}
                  fieldIndex={fieldIndex}
                  itemIndex={itemIndex}
                />
              }
              type={
                <div className="flex flex-1 flex-col">
                  <Controller
                    control={form.control}
                    defaultValue="text"
                    name={`attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.type`}
                    render={({ field: selectedField }) => {
                      const shouldDisplayAsTextarea =
                        fieldValues?.[fieldIndex]?.display_as_textarea;

                      const selectedValue =
                        selectedField.value === "text" &&
                        shouldDisplayAsTextarea
                          ? "multi_line_text"
                          : selectedField.value;

                      return (
                        <Select
                          dataLoc={`form-field-${fieldIndex}-type-selector`}
                          disabled={!getFieldPermission(fieldIndex, "type")}
                          options={FIELD_TYPE_OPTIONS}
                          value={selectedValue}
                          onChange={(type) => {
                            selectedField.onChange(
                              type && type === "multi_line_text"
                                ? "text"
                                : type,
                            );

                            if (type === "select" || type === "multi_select") {
                              const currentOptions = form.getValues(
                                `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options`,
                              );

                              if (!currentOptions?.length) {
                                form.setValue(
                                  `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options`,
                                  [""],
                                );
                              }
                            } // we're stripping out options before saving, so no need to clear them here

                            if (type === "multi_line_text") {
                              form.setValue(
                                `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.display_as_textarea`,
                                true,
                              );
                            } else {
                              form.unregister(
                                `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.display_as_textarea`,
                              );
                            }
                          }}
                        />
                      );
                    }}
                  />
                </div>
              }
            />
          </SortableField>
        ))}
      </SortableFieldsContext>

      {canAddField && (
        <div>
          <Button
            dataLoc="editor-add-field-button"
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={onAddField}
          >
            Add field
          </Button>
        </div>
      )}
    </div>
  );
};

type EditorFieldOptionsEditorProps = {
  basePath: BasePath;
  itemIndex: number;
  fieldIndex: number;
  canAddOption?: boolean;
  canDeleteOption?: boolean;
  canEditOption?: boolean;
};

const FieldOptionsEditorWrapper: React.FC<EditorFieldOptionsEditorProps> = ({
  basePath,
  itemIndex,
  fieldIndex,
  canAddOption = true,
  canDeleteOption = true,
  canEditOption = true,
}) => {
  return (
    <EditorFieldOptionsEditor
      basePath={basePath}
      canAddOption={canAddOption}
      canDeleteOption={canDeleteOption}
      canEditOption={canEditOption}
      fieldIndex={fieldIndex}
      itemIndex={itemIndex}
    />
  );
};

const EditorFieldOptionsEditor: React.FC<EditorFieldOptionsEditorProps> = ({
  basePath,
  itemIndex,
  fieldIndex,
  canAddOption = true,
  canDeleteOption = true,
  canEditOption = true,
}) => {
  const form = useFormContext<CaseTemplateFormValues>();
  const { errors } = useFormState<CaseTemplateFormValues>();

  const field = form.getValues(
    `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}`,
  );

  const optionsPath =
    `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options` as FieldPath<CaseTemplateFormValues>;

  const {
    fields: options,
    append,
    remove,
    move,
  } = useFieldArray({
    name: optionsPath,
    keyName: "_id",
  });

  const onAddOption = () => {
    append("", {
      focusName: `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options.${options.length}`,
    });
  };

  if (field?.type !== "select" && field?.type !== "multi_select") {
    return null;
  }

  return (
    <div className="ml-2.5 flex flex-col gap-y-2 border-gray-200 border-l pl-4">
      <SortableFieldsContext
        items={options.map((field) => field._id)}
        onChangeOrder={({ oldIndex, newIndex }) => {
          move(oldIndex, newIndex);
        }}
      >
        {options.map((option, optionIndex) => (
          <SortableField key={option._id} id={option._id}>
            <div>
              <div className="flex gap-x-2 align-middle">
                <Input
                  data-loc={`form-field-${fieldIndex}-option-${optionIndex}`}
                  disabled={!canEditOption}
                  {...form.register(
                    `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options.${optionIndex}`,
                  )}
                  errored={
                    !!getFieldError(
                      errors,
                      `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options.${optionIndex}` as FieldPath<CaseTemplateFormValues>,
                    )
                  }
                  placeholder="Option"
                  fullWidth
                />

                {canDeleteOption && (
                  <IconButton
                    dataLoc={`form-field-${fieldIndex}-option-${optionIndex}-delete-button`}
                    disabled={options.length <= 1}
                    icon={faTrashCan}
                    size="sm"
                    onClick={() => remove(optionIndex)}
                  />
                )}
              </div>

              <EditorFieldErrorHint
                keyPath={`attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.options.${optionIndex}`}
              />
            </div>
          </SortableField>
        ))}
      </SortableFieldsContext>
      {canAddOption && (
        <div>
          <Button
            dataLoc="add-option-button"
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={onAddOption}
          >
            Add option
          </Button>
        </div>
      )}
    </div>
  );
};

type EditorFieldErrorHintProps = {
  keyPath: string;
};

const EditorFieldErrorHint: React.FC<EditorFieldErrorHintProps> = ({
  keyPath,
}) => {
  const { errors } = useFormState<CaseTemplateFormValues>();
  const error = getFieldError(
    errors,
    keyPath as FieldPath<CaseTemplateFormValues>,
  );
  return error ? <ErrorHint>{error.message}</ErrorHint> : null;
};

type EditorFieldGroupFrameProps = {
  displayName: {
    input: React.ReactNode;
    error: React.ReactNode;
  };
  type: React.ReactNode;
  fieldKey: React.ReactNode;
  description: React.ReactNode;
  settings: React.ReactNode;
  dataLoc: string;
  deleteButton: React.ReactNode;
  optionsEditor?: React.ReactNode;
};

const EditorFieldGroupFrame: React.FC<EditorFieldGroupFrameProps> = ({
  displayName,
  type,
  settings,
  description,
  fieldKey,
  deleteButton,
  optionsEditor,
  dataLoc,
}) => (
  <div className="flex flex-col gap-y-2 align-middle" data-loc={dataLoc}>
    <div className="flex flex-col">
      <div className="flex flex-row items-center gap-x-2">
        {displayName.input}
        {type}
        {settings}
        {deleteButton}
      </div>
      {displayName.error}
    </div>
    {fieldKey}
    {description}
    {optionsEditor}
  </div>
);

type EditorFieldSettingsProps = {
  basePath: BasePath;
  itemIndex: number;
  fieldIndex: number;
  disabled?: boolean;
};

const EditorFieldSettings: React.FC<EditorFieldSettingsProps> = ({
  basePath,
  itemIndex,
  fieldIndex,
  disabled = false,
}) => {
  const form = useFormContext<CaseTemplateFormValues>();

  const requiredPath =
    `attributes.${basePath}.${itemIndex}.form.fields.${fieldIndex}.required` as FieldPath<CaseTemplateFormValues>;

  return (
    <CustomPopover
      button={
        <Icon
          variant={disabled ? "disabled" : "default"}
          dataLoc="field-settings-gear"
          icon={faCog}
          size="sm"
        />
      }
      className="w-44 px-3 py-4 font-inter-normal-13px text-gray-800"
      dataLoc="field-settings-popup"
      placement="bottom"
    >
      <div className="flex items-center justify-between">
        <span>Required</span>
        <Controller
          control={form.control}
          defaultValue={false}
          name={requiredPath}
          render={({ field }) => (
            <Switch
              dataLoc="field-settings-required"
              disabled={disabled}
              enabled={Boolean(field.value ?? false)}
              onChange={field.onChange}
            />
          )}
        />
      </div>
    </CustomPopover>
  );
};
